import { useContextProvider } from "./../context/providers/ContextProvider";
import { keepPreviousData, useMutation, useQueryClient } from "@tanstack/react-query";
import { useInfiniteQuery, useQuery } from "../_app/lib/query";
import { AjaxResponse } from "../_app/api";
import {
  assignUser,
  createUser,
  deleteUser,
  disableUser,
  doesHubUserExist,
  enableUser,
  fetchUserAttributes,
  getAccountsForLevel,
  getAllUsers,
  getUserById,
  registerUser,
  resendWelcomeEmail,
  updateCognitoUser,
  updateUser,
} from "./api";
import { RegisterUserParams, UserAccountAssignRequest, UserCreateUpdateRequest, UserId, UserListItem } from "./types";
import { AttributeType } from "@aws-sdk/client-cognito-identity-provider/dist-types/models/models_0";
import { mapToCognitoUser } from "./utils";
import { useFeedbackAlerts } from "../_app/hooks";
import { useAuthSession } from "../auth/hooks";

export function useUserRegister(options = {}) {
  return useMutation({
    mutationFn: (user: RegisterUserParams) => registerUser(user.accountCode, user.email),
    ...options,
  });
}

export function useHubUserExist(options = {}) {
  const { data: session } = useAuthSession();
  return useQuery({
    queryKey: ["hubUserExist", { id: session?.userSub }],
    queryFn: doesHubUserExist,
    enabled: Boolean(session?.userSub),
    ...options,
  });
}

export function useAllUsers(page: number, limit: number, options = {}) {
  const { setFeedbackAlertError } = useFeedbackAlerts();
  const { contextId: accountId } = useContextProvider();
  return useQuery({
    queryKey: ["users", { accountId, page, limit }],

    queryFn: async () => {
      const users = await getAllUsers(accountId, page, limit);
      return users;
    },

    placeholderData: keepPreviousData,

    onError: (error: any) => {
      setFeedbackAlertError(error?.data?.message || "Failed to fetch users");
    },

    ...options,
  });
}

export function useSingleUser(userId: string, options = {}) {
  const { setFeedbackAlertError } = useFeedbackAlerts();
  const { contextId: accountId } = useContextProvider();
  return useQuery({
    queryKey: ["user", { accountId, userId }],
    queryFn: () => getUserById(accountId, userId),
    ...options,

    onError: (error: AjaxResponse) => {
      setFeedbackAlertError(error?.data?.message);
    },
  });
}

export function useUserCreate(options: any = {}) {
  const queryClient = useQueryClient();
  const { setFeedbackAlertError, setFeedbackAlertSuccess } = useFeedbackAlerts();
  const { contextId: accountId } = useContextProvider();
  return useMutation({
    mutationFn: (user: UserCreateUpdateRequest) => createUser(accountId, user),
    ...options,

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["users"],
      });
      options?.onSuccess?.();
      setFeedbackAlertSuccess("User created");
    },

    onError: (error: AjaxResponse) => {
      setFeedbackAlertError(error?.data?.message);
    },
  });
}

export function useUserUpdate(userId: string, options: any = {}) {
  const queryClient = useQueryClient();
  const { setFeedbackAlertError, setFeedbackAlertSuccess } = useFeedbackAlerts();
  const { contextId: accountId } = useContextProvider();
  return useMutation({
    mutationFn: (user: UserCreateUpdateRequest) => updateUser(accountId, user, userId),
    ...options,

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["user", { accountId, userId }],
      });
      queryClient.invalidateQueries({
        queryKey: ["users"],
      });
      options?.onSuccess?.();
      setFeedbackAlertSuccess("User updated");
    },

    onError: (error: AjaxResponse) => {
      setFeedbackAlertError(error?.data?.message);
    },
  });
}

export function useUserAccountAssign(options: any = {}) {
  const queryClient = useQueryClient();
  const { setFeedbackAlertError, setFeedbackAlertSuccess } = useFeedbackAlerts();
  const { contextId: accountId } = useContextProvider();
  return useMutation({
    mutationFn: ({ user, userId }: { user: UserAccountAssignRequest; userId: UserId }) => assignUser(accountId, user, userId),
    ...options,

    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["user", { accountId, userId: variables.userId }],
      });
      queryClient.invalidateQueries({
        queryKey: ["users"],
      });
      options?.onSuccess?.();
      setFeedbackAlertSuccess("User created");
    },

    onError: (error: AjaxResponse) => {
      setFeedbackAlertError(error?.data?.message);
    },
  });
}

export function useCognitoUserAttributes(options = {}) {
  return useQuery({
    queryKey: ["userAttributes"],
    queryFn: () => fetchUserAttributes(),
    select: (data) => mapToCognitoUser(data.UserAttributes),
    ...options,
  });
}

export function useCognitoUserUpdate(options: any = {}) {
  const { setFeedbackAlertError, setFeedbackAlertSuccess } = useFeedbackAlerts();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (attributes: AttributeType[]) => updateCognitoUser(attributes),
    ...options,

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["userAttributes"],
      });
      setFeedbackAlertSuccess("User updated");
    },

    onError: () => {
      setFeedbackAlertError("Could not update user");
    },
  });
}

export function useUserDelete(options = {}) {
  const queryClient = useQueryClient();
  const { setFeedbackAlertError, setFeedbackAlertSuccess } = useFeedbackAlerts();
  const { contextId: accountId } = useContextProvider();
  return useMutation({
    mutationFn: (user: UserListItem) => deleteUser({ accountId, user }),
    ...options,

    onSuccess: (data: UserListItem) => {
      queryClient.invalidateQueries({
        queryKey: ["user", { accountId, userId: data?.id }],
      });
      queryClient.invalidateQueries({
        queryKey: ["users"],
      });
      setFeedbackAlertSuccess("User deleted");
    },

    onError: (error: AjaxResponse) => {
      setFeedbackAlertError(error?.data?.message);
    },
  });
}

export function useUserDisable(options = {}) {
  const queryClient = useQueryClient();
  const { contextId: accountId } = useContextProvider();
  return useMutation({
    mutationFn: (user: UserListItem) => disableUser({ accountId, user }),

    onSuccess: (data: UserListItem) => {
      queryClient.invalidateQueries({
        queryKey: ["user", { accountId, userId: data?.id }],
      });
      queryClient.invalidateQueries({
        queryKey: ["users"],
      });
    },

    ...options,
  });
}

export function useUserEnable(options = {}) {
  const queryClient = useQueryClient();
  const { contextId: accountId } = useContextProvider();
  return useMutation({
    mutationFn: (user: UserListItem) => enableUser(accountId, user),

    onSuccess: (data: UserListItem) => {
      queryClient.invalidateQueries({
        queryKey: ["user", { accountId, userId: data?.id }],
      });
      queryClient.invalidateQueries({
        queryKey: ["users"],
      });
    },

    ...options,
  });
}

export function useUserResendEmail(options = {}) {
  const { setFeedbackAlertSuccess, setFeedbackAlertError } = useFeedbackAlerts();
  const { contextId: accountId } = useContextProvider();
  return useMutation({
    mutationFn: (user: UserListItem) => resendWelcomeEmail(accountId, user),
    ...options,

    onSuccess: () => {
      setFeedbackAlertSuccess("Welcome email sent");
    },

    onError: (error: AjaxResponse) => {
      setFeedbackAlertError(error?.data?.message);
    },
  });
}

export function useAccountsForLevel(level: string, params: any, options = {}) {
  const { contextId: accountId } = useContextProvider();
  return useInfiniteQuery<any, AjaxResponse>({
    queryKey: ["accountsForLevel", { accountId, level, params }],
    queryFn: ({ pageParam }) => {
      if (level !== undefined || params?.parent !== "0") {
        return getAccountsForLevel(accountId, level, {
          offset: pageParam,
          ...params,
        });
      } else {
        return [];
      }
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage: any) => {
      const limit = lastPage?.pageSize * lastPage?.page + 1;
      return lastPage?.page < limit / lastPage?.pageSize && Boolean(lastPage?.list?.length)
        ? lastPage.pageSize + lastPage.page * limit
        : undefined;
    },
    ...options,
  });
}
